/**
 * Fades sections in as they scroll into view.
 *
 * Gated by the `scroll_reveal_enabled` theme setting, which adds
 * `scroll-reveal-on` to <body>; the paired CSS lives in base.css. Sections
 * start hidden and get `is-revealed` once, so the reveal never replays.
 *
 * Observing at the section level (rather than per block) keeps this to a single
 * observer and means no section schema has to opt in.
 */

const REVEALED = 'is-revealed';
const SELECTOR = '#MainContent .section';

/** Sections already above the fold should not animate in on load. */
const VIEWPORT_MARGIN = '0px 0px -10% 0px';

/** @type {IntersectionObserver | undefined} */
let observer;

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

function reveal(element) {
  element.classList.add(REVEALED);
  observer?.unobserve(element);
}

function add(section) {
  if (observed.has(section) || section.classList.contains(REVEALED)) return;
  observed.add(section);
  observer?.observe(section);
}

function observe(root = document) {
  if (!observer) return;

  // A re-rendered node can itself be a section, so match it as well as its
  // descendants - querySelectorAll alone would skip it.
  if (root instanceof Element && root.matches(SELECTOR)) add(root);

  for (const section of root.querySelectorAll(SELECTOR)) add(section);
}

function init() {
  // Without IntersectionObserver, or with reduced motion, show everything
  // immediately rather than leaving sections stuck at opacity 0.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!('IntersectionObserver' in window) || prefersReducedMotion) {
    for (const section of document.querySelectorAll(SELECTOR)) {
      section.classList.add(REVEALED);
    }
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { rootMargin: VIEWPORT_MARGIN }
  );

  // The first section is on screen before any script runs. Fading it in would
  // delay the largest contentful paint for no visual gain, so reveal it outright
  // and let its own blocks handle the entrance (see the hero rules in base.css).
  document.querySelector(SELECTOR)?.classList.add(REVEALED);

  observe();

  // Sections swapped in by section rendering (theme editor edits, filtering,
  // paginated lists) need picking up too.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) observe(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
